import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, Spinner } from '@structural-climbing/ui'

interface Gym {
  id: string
  name: string
  slug: string
  description: string | null
  image_url: string | null
  accepting_members: boolean
}

export function GymDiscovery({ excludeIds }: { excludeIds?: string[] } = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [allGyms, setAllGyms] = useState<Gym[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${apiUrl}/gyms`)
      .then(async (res) => {
        if (res.ok) setAllGyms((await res.json()) as Gym[])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const excluded = new Set(excludeIds ?? [])
  const gyms = allGyms.filter((g) => !excluded.has(g.id))

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }

  if (!gyms.length) {
    return <p className="py-4 text-center text-sm text-foreground/40">{t('gymDiscovery.noGyms')}</p>
  }

  const open = gyms.filter((g) => g.accepting_members)
  const comingSoon = gyms.filter((g) => !g.accepting_members)

  return (
    <div className="flex flex-col gap-3">
      {open.length > 0 && (
        <p className="text-sm font-medium text-foreground/70">{t('gymDiscovery.findGym')}</p>
      )}
      {open.map((gym) => (
        <Card key={gym.id} isPressable onPress={() => void navigate(`/join/${gym.slug}`)}>
          <GymRow gym={gym}>
            <span className="shrink-0 text-xs font-medium text-primary">
              {t('gymDiscovery.joinArrow')}
            </span>
          </GymRow>
        </Card>
      ))}

      {/* Gyms still being set up. Listed so members know they are coming, but
          not pressable: the join-request endpoint refuses them until a super
          admin opens them. */}
      {comingSoon.length > 0 && (
        <p className={`text-sm font-medium text-foreground/70 ${open.length > 0 ? 'mt-3' : ''}`}>
          {t('gymDiscovery.comingSoonLabel')}
        </p>
      )}
      {comingSoon.map((gym) => (
        <Card key={gym.id} className="opacity-60">
          <GymRow gym={gym}>
            <span className="shrink-0 text-xs font-medium text-foreground/50">
              {t('gymDiscovery.comingSoonBadge')}
            </span>
          </GymRow>
        </Card>
      ))}
    </div>
  )
}

function GymRow({ gym, children }: { gym: Gym; children: ReactNode }) {
  return (
    <CardBody className="flex flex-row items-center gap-3 py-3">
      {gym.image_url ? (
        <img
          src={gym.image_url}
          alt={gym.name}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-content2 text-lg font-bold text-foreground/30">
          {gym.name[0]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{gym.name}</p>
        {gym.description && (
          <p className="truncate text-xs text-foreground/50">{gym.description}</p>
        )}
      </div>
      {children}
    </CardBody>
  )
}
